import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api.js';
import { errMessage } from '../i18n.js';
import { useT } from '../lang.js';
import PageSkeleton from '../Skeleton.jsx';
import Modal from '../Modal.jsx';
import Icon from '../Icon.jsx';

// Anahtar adı aynı zamanda çeviri anahtarı: `set.<alan>`.
const TOGGLES = [
  'staffMayOpenTable', 'guestMayOpenTable', 'requireStaffApproval',
  // Garsonun kendi girdiği yanlışı düzeltmesi; kapalıyken kasiyer+ gerekir.
  'waiterMayEditOrder',
  'cashEnabled', 'splitEnabled', 'guestSelfCheckout',
  'tipsEnabled', 'waiterCallEnabled', 'autoPrintOnOrder',
];

export default function Settings() {
  const t = useT();
  const [s, setS] = useState(null);
  const [rest, setRest] = useState(null);
  const [role, setRole] = useState(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // TEST ödeme kipi onayı: açarken de kapatırken de ne olacağı yazılı
  // anlatılır — bu kip "ayar" değil, paranın akıp akmayacağı kararıdır.
  const [demoAsk, setDemoAsk] = useState(null); // null | 'on' | 'off'

  useEffect(() => {
    api('/api/restaurant').then((d) => { setS(d.settings); setRest(d.restaurant); setRole(d.me.role); })
      .catch((e) => setErr(errMessage(t, e)));
    // eslint-disable-next-line
  }, []);

  async function save(patch) {
    setErr(''); setMsg(''); setBusy(true);
    try {
      const r = await api('/api/restaurant/settings', { method: 'PATCH', body: patch });
      setS(r.settings);
      setMsg(t('set.saved'));
      // Üst çubuktaki TEST kipi rozeti aynı anda güncellensin.
      window.dispatchEvent(new CustomEvent('orderceo:settings-changed'));
    } catch (e) {
      setErr(errMessage(t, e));
      // Sunucu reddettiyse ekranı sunucunun gerçeğine geri çek
      api('/api/restaurant').then((d) => setS(d.settings)).catch(() => {});
    } finally {
      setBusy(false);
    }
  }

  /** Restoran künyesi ayrı uçta (/api/restaurant) — ayarlarla aynı kart değil. */
  async function saveProfile(patch) {
    setErr(''); setMsg(''); setBusy(true);
    try {
      const r = await api('/api/restaurant', { method: 'PATCH', body: patch });
      setRest((cur) => ({ ...cur, ...patch, ...r.restaurant }));
      setMsg(t('set.saved'));
    } catch (e) {
      setErr(errMessage(t, e));
      api('/api/restaurant').then((d) => setRest(d.restaurant)).catch(() => {});
    } finally {
      setBusy(false);
    }
  }

  if (err && !s) return <p className="err">{err}</p>;
  if (!s) return <PageSkeleton shape="form" />;
  const readOnly = !['OWNER', 'MANAGER'].includes(role);
  // TEST kipi SAHİBİN kararı (sunucu da OWNER arıyor); "canlı" = Stripe
  // tahsilat hesabı hazır, yani şu an gerçek para alınabiliyor.
  const isOwner = role === 'OWNER';
  const live = rest?.connectStatus === 'COMPLETED';

  return (
    <>
      {readOnly && <div className="card"><p className="muted" style={{ margin: 0 }}>{t('readOnlyRole')}</p></div>}

      {/* Künye en üstte: adı basılı menüde, fişte ve misafir ekranında görünen
          tek yer burası — "nereden değiştiriliyor" sorusu sorulmasın. */}
      <div className="card">
        <h2>{t('set.profileTitle')}</h2>
        <div className="grid2">
          <div>
            <label>{t('set.restaurantName')}</label>
            <input defaultValue={rest?.name || ''} disabled={readOnly || busy} maxLength={80}
              key={`n${rest?.name}`}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (!v) { e.target.value = rest?.name || ''; return setErr(t('set.nameRequired')); }
                if (v !== rest?.name) saveProfile({ name: v });
              }} />
          </div>
          <div>
            <label>{t('set.phone')}</label>
            <input defaultValue={rest?.phone || ''} disabled={readOnly || busy} maxLength={40}
              key={`p${rest?.phone}`}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v !== (rest?.phone || '')) saveProfile({ phone: v || null });
              }} />
          </div>
        </div>
        <p className="muted" style={{ marginBottom: 0 }}>{t('set.profileNote')}</p>
      </div>

      <div className="card">
        <h2>{t('set.modeTitle')}</h2>
        <label>{t('set.mode')}</label>
        <select value={s.orderingMode} disabled={readOnly || busy}
          onChange={(e) => save({ orderingMode: e.target.value })}>
          <option value="PAY_FIRST">{t('set.payFirst')}</option>
          <option value="OPEN_TABLE">{t('set.openTable')}</option>
        </select>
        <p className="muted">{t('set.modeNote')}</p>

        <label style={{ marginTop: 8 }}>{t('set.ttl')}</label>
        <input type="number" min="30" max="900" defaultValue={s.reservationTtlSeconds} disabled={readOnly || busy}
          onBlur={(e) => {
            const v = Number(e.target.value);
            if (v !== s.reservationTtlSeconds) save({ reservationTtlSeconds: v });
          }} />
        <p className="muted" style={{ marginBottom: 0 }}>{t('set.ttlNote')}</p>
      </div>

      <div className="card">
        <h2>{t('set.switches')}</h2>
        {TOGGLES.map((k) => (
          <div className="switch" key={k} style={{ padding: '6px 0', borderBottom: '1px solid var(--line)' }}>
            <input type="checkbox" checked={!!s[k]} disabled={readOnly || busy}
              onChange={(e) => save({ [k]: e.target.checked })} />
            <span>{t(`set.${k}`)}</span>
          </div>
        ))}
      </div>

      <div className="card">
        <h2>{t('set.receiptFooter')}</h2>
        <textarea rows={3} defaultValue={s.receiptFooterText || ''} disabled={readOnly || busy}
          onBlur={(e) => save({ receiptFooterText: e.target.value })} />
      </div>

      {/* ── TEST ödeme kipi ────────────────────────────────────────────
          En altta ve kendi kartında: günlük bir ayar değil, kurulum
          sırasında bir kez açılıp Stripe gelince kapatılan bir kip. */}
      <div className="card" style={s.demoPayments ? { borderColor: 'var(--warn)' } : null}>
        <div className="row">
          <h2 style={{ margin: 0 }}>{t('set.demoTitle')}</h2>
          <span className={`badge ${s.demoPayments ? 'warn' : ''}`}>
            {s.demoPayments ? t('on') : t('off')}
          </span>
          <span className="spacer" />
          <button className={s.demoPayments ? '' : 'primary'} disabled={!isOwner || busy}
            onClick={() => setDemoAsk(s.demoPayments ? 'off' : 'on')}>
            <Icon n={s.demoPayments ? 'checkCircle' : 'playCircle'} />
            {s.demoPayments ? t('set.demoTurnOff') : t('set.demoTurnOn')}
          </button>
        </div>
        <p className="muted" style={{ marginBottom: 0 }}>
          {t('set.demoLead')}{!isOwner && <> {t('set.demoOwnerOnly')}</>}
        </p>
      </div>

      {demoAsk && (
        <Modal
          title={demoAsk === 'on' ? t('set.demoOnTitle') : t('set.demoOffTitle')}
          onClose={() => setDemoAsk(null)}
          footer={
            <div className="row">
              <button onClick={() => setDemoAsk(null)}><Icon n="x" />{t('cancel')}</button>
              <span className="spacer" />
              <button className="primary" disabled={busy} onClick={() => {
                setDemoAsk(null);
                save({ demoPayments: demoAsk === 'on' });
              }}>
                <Icon n="check" />
                {demoAsk === 'on' ? t('set.demoConfirmOn') : t('set.demoConfirmOff')}
              </button>
            </div>
          }>
          {demoAsk === 'on' ? (
            <>
              {/* Yayındaki restoranda bu kip AÇILIRSA gerçek tahsilat durur:
                  uyarı listenin ÜSTÜNDE ve ayrı renkte. */}
              {live && <p className="err"><b>{t('set.demoLiveWarn')}</b></p>}
              <p>{t('set.demoOnWhat')}</p>
              <ul>
                <li>{t('set.demoP1')}</li>
                <li>{t('set.demoP2')}</li>
                <li>{t('set.demoP3')}</li>
                <li>{t('set.demoP4')}</li>
                <li>{t('set.demoP5')}</li>
              </ul>
            </>
          ) : (
            <>
              <p>{t('set.demoOffWhat')}</p>
              <ul>
                <li>{t('set.demoOffP1')}</li>
                <li>{t('set.demoOffP2')}</li>
                <li>{t('set.demoOffP3')}</li>
              </ul>
              {!live && (
                <p className="muted">
                  <Link to="/payments">{t('set.demoGoSetup')}</Link>
                </p>
              )}
            </>
          )}
        </Modal>
      )}

      {msg && <p className="ok">{msg}</p>}
      {err && <p className="err">{err}</p>}
    </>
  );
}
